// Season editor "Advance" section: Edit Field's own KC table, planner,
// fertigation and meteorological stations components, on the season parsed as
// V1 parses a field (GeneralClasses.Field, with the form's changes applied).
// Each handler records what UpdateFieldModal's handler sets, as a Field path
// edit ({path: value}, seasonHelpers.isFieldPath), which seasonSaveBody applies
// to the Field before building the V1 body.
//
// The section is a <form> so "Save changes" can run the same HTML validity
// checks Edit Field's form runs on these inputs (useAdvanceSection.check).
const React = require('react');
const { useState, useRef, useCallback } = require('react');
const { useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const KCBox = require('KCBox');
const Fertigation = require('Fertigation');
const { PlannerWrapper } = require('PlannerWrapper');
const MeteorologicalData = require('../dashboard/field/MeteorologicalData');
const {
  addStation,
  removeStationAt,
  moveStation,
} = require('../dashboard/field/meteorologicalStationsHelpers');
const fieldManager = require('fieldManager');

const noop = () => {};

// UpdateFieldModal.handleFertigationConcentrationChange: the rows as stored.
const plainFertigationConcentrations = (fcs) =>
  fcs.map((row) => ({
    start_date: row.startDate.format('YYYY-MM-DD'),
    fertType: row.fertType,
    concentration: parseFloat(row.concentration),
  }));

// Edit Field's handlers, each as the Field path it sets.
const useAdvanceHandlers = (field, onFieldEdit, onCropDetailsChange) => ({
  onKCTableChange: (kcTable) => onFieldEdit('crop_data.kc_table', kcTable),
  onCropDetailsChange,
  handleSettingsChange: (key, value) => onFieldEdit('settings.' + key, value),
  handlePlannerSettingsChange: (key, value) =>
    onFieldEdit('settings.planner_settings', {
      ...field.settings.planner_settings,
      [key]: value,
    }),
  handleIrrigationTimeFramesChange: (key, timeFrames) =>
    onFieldEdit('irrigation_system.' + key, timeFrames),
  onFertigationConcentrationChange: (fcs) =>
    onFieldEdit(
      'irrigation_system.fertigation_concentrations',
      plainFertigationConcentrations(fcs),
    ),
  onConcentrationChange: (concentrations) =>
    onFieldEdit('initial_concentrations', concentrations),
  onStationAdd: (stationId) =>
    onFieldEdit(
      'geo_data.meteorological_stations',
      addStation(field.geo_data?.meteorological_stations, stationId),
    ),
  onStationRemove: (index) =>
    onFieldEdit(
      'geo_data.meteorological_stations',
      removeStationAt(field.geo_data?.meteorological_stations, index),
    ),
  onStationMove: (from, to) =>
    onFieldEdit(
      'geo_data.meteorological_stations',
      moveStation(field.geo_data?.meteorological_stations, from, to),
    ),
});

const AdvanceBody = ({
  field,
  isModerator,
  farmingUnitSensors,
  formRef,
  hidden,
  onFieldEdit,
  onCropDetailsChange,
  onEditingChange,
}) => {
  const userData = useSelector((state) => state.authentication_data?.user_data);
  const handlers = useAdvanceHandlers(field, onFieldEdit, onCropDetailsChange);
  return (
    <form ref={formRef} className='ns-advance-body' hidden={hidden} noValidate>
      <KCBox
        kcTableOnly
        selectedField={field}
        onKCTableChange={handlers.onKCTableChange}
        onCropDetailsChange={handlers.onCropDetailsChange}
        onIrrigationDateChange={noop}
        onSowDateChange={noop}
        onHarvestDateChange={noop}
        disableCropType={false}
        isModerator={isModerator}
      />
      <PlannerWrapper
        key={field.id}
        field={field}
        handleSettingsChange={handlers.handleSettingsChange}
        handleIrrigationTimeFramesChange={
          handlers.handleIrrigationTimeFramesChange
        }
        handlePlannerSettingsChange={handlers.handlePlannerSettingsChange}
        onTimeFrameEditingChange={onEditingChange}
        userData={userData}
      />
      <Fertigation
        key={'fertigation-' + field.id}
        initial_concentrations={field.initial_concentrations}
        isModerator={isModerator}
        onFertigationConcentrationChange={
          handlers.onFertigationConcentrationChange
        }
        onConcentrationChange={handlers.onConcentrationChange}
        initialFertigationConcentrations={
          field.irrigation_system.fertigation_concentrations
        }
        shouldFertigationConcentrationBeDisabled={fieldManager.getIfThisFieldSupportFert(
          field.sensors,
          farmingUnitSensors,
        )}
      />
      {/* Keyed by field so switching block/season starts with empty pickers. */}
      <MeteorologicalData
        key={field?.id}
        field={field}
        onStationAdd={handlers.onStationAdd}
        onStationRemove={handlers.onStationRemove}
        onStationMove={handlers.onStationMove}
      />
    </form>
  );
};

// Open state and Edit Field's form check for the advanced inputs. The body
// stays mounted while collapsed, so the check covers it as Edit Field's form
// always does; an invalid input opens the section and is marked as Edit Field
// marks it. `check()` is false when any is invalid.
const useAdvanceSection = () => {
  const [isOpen, setIsOpen] = useState(false);
  const formRef = useRef(null);
  const toggle = useCallback(() => setIsOpen((open) => !open), []);
  const check = useCallback(() => {
    const form = formRef.current;
    if (!form) {
      return true;
    }
    form.classList.add('was-validated');
    const isValid = form.checkValidity();
    if (!isValid) {
      setIsOpen(true);
    }
    return isValid;
  }, []);
  return { isOpen, toggle, formRef, check };
};

// `field`: the parsed season, or null when it cannot be edited here (bulk
// edit, a pre-season not created yet, or its detail still loading); then
// `unavailableMessage` says why. `advance`: useAdvanceSection().
const SeasonAdvanceSection = ({
  field,
  unavailableMessage,
  advance,
  ...bodyProps
}) => {
  const { t } = useTranslation();
  const { isOpen, toggle, formRef } = advance;
  return (
    <div className='ns-section ns-advance'>
      <button
        type='button'
        className='ns-section-title ns-advance-toggle'
        aria-expanded={isOpen}
        onClick={toggle}
      >
        <span className={'ns-advance-chevron' + (isOpen ? ' is-open' : '')}>
          ›
        </span>{' '}
        {t('new_season_section_advance')}
      </button>
      {field ? (
        <AdvanceBody
          field={field}
          formRef={formRef}
          hidden={!isOpen}
          {...bodyProps}
        />
      ) : (
        isOpen && (
          <div className='ns-muted ns-advance-unavailable'>
            {unavailableMessage}
          </div>
        )
      )}
    </div>
  );
};

module.exports = { SeasonAdvanceSection, useAdvanceSection };
